import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { ModalButtonWithForm, User } from 'components/common';
import {
  EmailMessageEditor,
  FormGroup,
  RadioGroup
} from 'components/common/form';

import type { EmailTemplateData, User as UserProps } from 'shared-types';

interface Props {
  emailTemplates: EmailTemplateData[];
  emailTemplatesDefault: EmailTemplateData;
  targetUser?: UserProps;
  updateUsers: (props?: any) => Promise<void> | void;
}

const GroupAttributeUsersInvite = ({
  emailTemplates,
  emailTemplatesDefault,
  targetUser,
  updateUsers
}: Props) => {
  const { dispatch } = useDispatch();
  const {
    messages,
    pageState: { group }
  } = useReduxState();

  const submit = async (values) => {
    const response = await api.post(`/admin/groups/${group.id}/users-invite`, {
      emailAddress: targetUser ? targetUser.email : null,
      emailContent: values.email,
      recipientsType: values.recipientsType,
      usersType: 'attribute-users'
    });
    await updateUsers();

    let successMessage = `${response.data.sentEmails} ${messages.commonSendEmailInvitesSuccess}`;

    let isWarning = false;
    const errorEmails = Object.keys(response.data.errors);

    if (errorEmails && errorEmails.length) {
      isWarning = true;
      successMessage += `<br /><br /> ${messages.commonSendEmailInvitesError}`;
      errorEmails.forEach((errorEmail) => {
        successMessage += `<br />${errorEmail}`;
      });
    }

    dispatch.addNotice({
      text: successMessage,
      type: isWarning ? 'warning' : 'success'
    });
  };

  if (!emailTemplatesDefault) return null;

  const initialValues = {
    email: {
      ...emailTemplatesDefault.email,
      selectedTemplateIdentifier: emailTemplatesDefault.identifier
    },
    recipientsType: 'new-and-uninvited'
  };

  return (
    <ModalButtonWithForm
      buttonIcon="paper-plane"
      buttonSize="header"
      buttonText={
        targetUser
          ? messages.commonSendEmailInvite
          : messages.commonSendEmailInvites
      }
      initialValues={initialValues}
      modalActionText={messages.commonSend}
      modalTitle={
        targetUser
          ? messages.commonSendEmailInvite
          : messages.commonSendEmailInvites
      }
      modalWidth="wide"
      onSubmit={submit}
    >
      {() => (
        <div>
          {targetUser ? (
            <FormGroup label={messages.commonRecipient}>
              <User enableProfileLink={true} user={targetUser} />
            </FormGroup>
          ) : (
            <FormGroup label={messages.emailRecipientsSend}>
              <RadioGroup
                field="recipientsType"
                options={[
                  {
                    id: 'attribute-email-recipients-all',
                    label: messages.emailRecipientsAll,
                    value: 'all'
                  },
                  {
                    id: 'attribute-email-recipients-uninvited',
                    label: messages.emailRecipientsUninvited,
                    value: 'uninvited'
                  },
                  {
                    id: 'attribute-email-recipients-new',
                    label: messages.emailRecipientsNewAndUninvited,
                    value: 'new-and-uninvited'
                  }
                ]}
              />
            </FormGroup>
          )}

          <EmailMessageEditor
            availableTemplates={emailTemplates}
            id="group-attribute-users-invite"
            style={{ marginTop: 20 }}
          />
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default GroupAttributeUsersInvite;
